import { useState, useEffect, useCallback } from "react";
import { SlidePnl, shootToast, TableFooter, TablePagination, TableShowPages } from "codekit";
import { generatePagination } from "../../../../../utils/generatePagination";
import {
  useGetCliReportMutation,
  useGetCliKpiItemsMutation,
  useGetCliKpiDeclaracionesMutation,
  useGetCliKpiInspAutoMutation,
  useGetCliKpiInspFisMutation,
  useGetCliKpiInspDocMutation,
  useGenerateConsolidadoExcelMutation,
} from "../../../../../libs/redux/slices/Graficsx/Ingeprop/ingepropApiSlice";
import {
  useGetEntregauGerenciasQuery,
  useLazyGetEntregauGruposQuery,
} from "../../../../../libs/redux/slices/Graficsx/Entregau/entregauApiSlice";

const TH = {
  textAlign: "left",
  padding: "8px 10px",
  background: "#1B2F6E",
  color: "#fff",
  fontSize: 10,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: ".05em",
  whiteSpace: "nowrap",
  position: "sticky",
  top: 0,
  zIndex: 5,
};
const TD = { padding: "9px 10px", borderBottom: "1px solid #F0F2F7", fontSize: 11 };
const TDR = { ...TD, textAlign: "right", fontVariantNumeric: "tabular-nums" };
const THP = { ...TH, background: "#243C7E" };

const TABS = [
  { key: "", label: "Todos" },
  { key: "OTROS", label: "Otros" },
  { key: "EXPORTACION", label: "Exportación" },
  { key: "IMPORTACION", label: "Importación" },
  { key: "TRANSITO", label: "Tránsito" },
];

const COMPARE_OPTS = [
  { value: "none", label: "Sin comparativo" },
  { value: "period", label: "Mismo periodo año anterior" },
  { value: "month_aa", label: "Mes anterior vs mismo mes AA" },
  { value: "quarter", label: "Trimestre actual vs anterior" },
  { value: "semester", label: "Semestre actual vs anterior" },
  { value: "ytd_aa", label: "Año anterior completo vs YTD" },
];

const TOP_OPTS = [
  { value: 0,  label: "Todos los clientes" },
  { value: 10, label: "Top 10" },
  { value: 20, label: "Top 20" },
  { value: 50, label: "Top 50" },
];

const fmt = (n) => "$ " + new Intl.NumberFormat("de-DE").format(Math.round(n ?? 0));
const fmtN = (n) => new Intl.NumberFormat("de-DE").format(n ?? 0);

/**
 * Desplaza FECHINIT y FECHFINT un año hacia atrás para el comparativo
 * @param {object} filters - filtros originales con FECHINIT y FECHFINT
 * @returns {object} filtros con fechas del mismo periodo del año anterior
 */
const shiftYear = (filters) => {
  const shift = (d) => {
    if (!d) return d;
    const dt = new Date(d);
    dt.setFullYear(dt.getFullYear() - 1);
    return dt.toISOString().slice(0, 10);
  };
  return { ...filters, FECHINIT: shift(filters?.FECHINIT), FECHFINT: shift(filters?.FECHFINT) };
};

/**
 * Construye un mapa [CLIIDXXX|TIPOFILT => INGPROPX] para búsqueda rápida de datos anteriores
 * @param {array} prevRows - filas del periodo anterior
 * @returns {object} mapa indexado
 */
const buildPrevMap = (prevRows) => {
  const m = {};
  prevRows.forEach((r) => {
    m[`${r.CLIIDXXX}|${r.TIPOFILT}`] = r.INGPROPX;
  });
  return m;
};

const isoDate = (y, m, d) => {
  const dt = new Date(y, m, d);
  return [dt.getFullYear(), String(dt.getMonth() + 1).padStart(2, "0"), String(dt.getDate()).padStart(2, "0")].join(
    "-",
  );
};

/**
 * Calcula los filtros del periodo de comparación según el modo seleccionado
 * @param {string} mode    - opción de comparativo (period | month_aa | quarter | semester | ytd_aa)
 * @param {object} filters - filtros actuales con FECHINIT y FECHFINT
 * @returns {object|null}  filtros del periodo de comparación
 */
const computePrevFilters = (mode, filters) => {
  const d = filters?.FECHFINT ? new Date(filters.FECHFINT) : new Date();
  const y = d.getFullYear();
  const m = d.getMonth();
  if (mode === "period") {
    return shiftYear(filters);
  }
  if (mode === "month_aa") {
    return { ...filters, FECHINIT: isoDate(y - 1, m, 1), FECHFINT: isoDate(y - 1, m + 1, 0) };
  }
  if (mode === "quarter") {
    const pq = (Math.floor(m / 3) + 3) % 4;
    const py = m < 3 ? y - 1 : y;
    return { ...filters, FECHINIT: isoDate(py, pq * 3, 1), FECHFINT: isoDate(py, pq * 3 + 3, 0) };
  }
  if (mode === "semester") {
    const ph = m < 6 ? 1 : 0;
    const py = m < 6 ? y - 1 : y;
    return { ...filters, FECHINIT: isoDate(py, ph * 6, 1), FECHFINT: isoDate(py, ph * 6 + 6, 0) };
  }
  if (mode === "ytd_aa") {
    return { ...filters, FECHINIT: `${y - 1}-01-01`, FECHFINT: `${y - 1}-12-31` };
  }
  return null;
};

const selectFt = {
  background: "#fff",
  color: "#1B2F6E",
  border: "1px solid #E6E6E6",
  padding: "5px 10px",
  borderRadius: 4,
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
};

const btnFt = {
  border: "none",
  borderRadius: 6,
  padding: "6px 14px",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
};

const FiltTop = ({ topN, onTopN, gerOpts, gerFilter, onGer, grupOpts, grupFilter, onGrup, onApply, onClear }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 20px", background: "#F0F4FB", borderBottom: "1px solid #E6E6E6", flexWrap: "wrap" }}>
    <span style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: ".04em" }}>
      Filtros top:
    </span>
    <select value={topN} onChange={(e) => onTopN(Number(e.target.value))} style={selectFt}>
      {TOP_OPTS.map((o, i) => (
        <option key={i} value={o.value}>{o.label}</option>
      ))}
    </select>
    <select value={gerFilter} onChange={(e) => onGer(e.target.value)} style={{ ...selectFt, minWidth: 200 }}>
      {gerOpts.map((o, i) => (
        <option key={i} value={o.value}>{o.text}</option>
      ))}
    </select>
    <select value={grupFilter} onChange={(e) => onGrup(e.target.value)} style={{ ...selectFt, minWidth: 200 }}>
      {grupOpts.map((o, i) => (
        <option key={i} value={o.value}>{o.text}</option>
      ))}
    </select>
    <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
      <button onClick={onClear} style={{ ...btnFt, background: "#E8472A", color: "#fff" }}>
        Limpiar
      </button>
      <button onClick={onApply} style={{ ...btnFt, background: "#1B2F6E", color: "#fff" }}>
        Aplicar
      </button>
    </div>
  </div>
);

const Badge = ({ tipo }) => (
  <span
    style={{
      background: tipo === "IMPORTACION" ? "#DBEAFE" : tipo === "EXPORTACION" ? "#D1FAE5" : "#F1F5F9",
      color: tipo === "IMPORTACION" ? "#1E40AF" : tipo === "EXPORTACION" ? "#166534" : "#64748B",
      padding: "2px 8px",
      borderRadius: 10,
      fontSize: 10,
      fontWeight: 700,
    }}
  >
    {tipo}
  </span>
);

const VarCell = ({ cur, prev, dark }) => {
  if (prev === null || prev === undefined) return <span style={{ color: "#94A3B8" }}>—</span>;
  const diff = cur - prev;
  const pct = prev > 0 ? Math.abs((diff / prev) * 100).toFixed(1) : null;
  const isUp = diff >= 0;
  const color = dark ? (isUp ? "#6EE7B7" : "#FCA5A5") : isUp ? "#16A34A" : "#DC2626";
  return (
    <span style={{ color, fontWeight: 700, fontSize: 11, whiteSpace: "nowrap" }}>
      {isUp ? "▲" : "▼"} {pct !== null ? `${pct}%` : ""}
    </span>
  );
};

const KpiBar = ({ kpis }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: `repeat(${kpis.length}, 1fr)`,
      borderBottom: "1px solid #E6E6E6",
      background: "#fff",
    }}
  >
    {kpis.map((k) => (
      <div key={k.label} style={{ padding: "10px 10px", borderRight: "1px solid #E6E6E6", textAlign: "center" }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: k.loading ? "#CBD5E1" : "#1B2F6E" }}>
          {k.loading ? "···" : k.value}
        </div>
        <div
          style={{ fontSize: 9, color: "#64748B", textTransform: "uppercase", letterSpacing: ".04em", marginTop: 3 }}
        >
          {k.label}
        </div>
      </div>
    ))}
  </div>
);

const TabRow = ({ tabs, active, onSelect }) => (
  <div
    style={{
      display: "flex",
      gap: 0,
      borderBottom: "1px solid #E6E6E6",
      background: "#fff",
      position: "sticky",
      top: 0,
      zIndex: 5,
    }}
  >
    {tabs.map((t) => (
      <button
        key={t.key}
        onClick={() => onSelect(t.key)}
        style={{
          padding: "11px 18px",
          border: "none",
          background: "none",
          cursor: "pointer",
          fontSize: 13,
          fontWeight: active === t.key ? 700 : 500,
          color: active === t.key ? "#1B2F6E" : "#64748B",
          borderBottom: active === t.key ? "3px solid #E8472A" : "3px solid transparent",
        }}
      >
        {t.label}{" "}
        <span
          style={{
            fontSize: 10,
            background: active === t.key ? "#1B2F6E" : "#F1F5F9",
            color: active === t.key ? "#fff" : "#475569",
            padding: "1px 6px",
            borderRadius: 10,
            marginLeft: 4,
          }}
        >
          {t.count}
        </span>
      </button>
    ))}
  </div>
);

const TotalRow = ({ rows, prevMap }) => {
  const totIng = rows.reduce((s, r) => s + (r.INGPROPX ?? 0), 0);
  const totDos = rows.reduce((s, r) => s + (r.TOTDOSXX ?? 0), 0);
  const totPrev = prevMap ? rows.reduce((s, r) => s + (prevMap[`${r.CLIIDXXX}|${r.TIPOFILT}`] ?? 0), 0) : null;
  const td = {
    padding: "12px 10px",
    fontSize: 13,
    fontWeight: 700,
    background: "linear-gradient(90deg,#1B2F6E,#2C4080)",
    color: "#fff",
  };
  return (
    <tr>
      <td style={td}>★</td>
      <td style={{ ...td, minWidth: 200 }}>TOTAL ACUMULADO</td>
      <td style={td}>—</td>
      <td style={{ ...td, textAlign: "right" }}>{fmt(totIng)}</td>
      <td style={{ ...td, textAlign: "right" }}>{fmtN(totDos)}</td>
      {prevMap !== null && <td style={{ ...td, textAlign: "right", opacity: 0.8 }}>{fmt(totPrev)}</td>}
      {prevMap !== null && (
        <td style={{ ...td, textAlign: "right" }}>
          <VarCell cur={totIng} prev={totPrev} dark />
        </td>
      )}
      <td style={td}>—</td>
      <td style={td}>—</td>
    </tr>
  );
};

const ColHdr = ({ hasCmp }) => (
  <thead>
    <tr>
      <th style={{ ...TH, minWidth: 90 }}>NIT</th>
      <th style={{ ...TH, minWidth: 200 }}>Cliente</th>
      <th style={TH}>Tipo Ingreso</th>
      <th style={{ ...TH, textAlign: "right" }}>Ingresos Propios</th>
      <th style={{ ...TH, textAlign: "right" }}>Total DOS</th>
      {hasCmp && <th style={{ ...THP, textAlign: "right" }}>Ing. AA</th>}
      {hasCmp && <th style={{ ...THP, textAlign: "right" }}>Var. %</th>}
      <th style={TH}>Grupo de Gestión</th>
      <th style={TH}>Líder</th>
    </tr>
  </thead>
);

const DataRow = ({ r, i, prevMap }) => {
  const prevIng = prevMap !== null ? (prevMap[`${r.CLIIDXXX}|${r.TIPOFILT}`] ?? null) : undefined;
  return (
    <tr style={{ background: i % 2 === 0 ? "#fff" : "#FAFBFD" }}>
      <td style={TD}>{r.CLIIDXXX}</td>
      <td style={{ ...TD, fontWeight: 600, color: "#1B2F6E", minWidth: 200 }}>{r.CLINOMXX}</td>
      <td style={TD}>
        <Badge tipo={r.TIPOINGE} />
      </td>
      <td style={TDR}>{fmt(r.INGPROPX)}</td>
      <td style={TDR}>{fmtN(r.TOTDOSXX)}</td>
      {prevMap !== null && (
        <td style={{ ...TDR, background: "#F0F2F7", color: "#94A3B8", fontStyle: "italic" }}>
          {prevIng !== null ? fmt(prevIng) : "—"}
        </td>
      )}
      {prevMap !== null && (
        <td style={TDR}>
          <VarCell cur={r.INGPROPX} prev={prevIng} />
        </td>
      )}
      <td style={TD}>{r.GRUPDESX}</td>
      <td style={TD}>{r.LIDERNOX}</td>
    </tr>
  );
};

const DataTbl = ({ rows, allRows, activeTab, prevMap }) => {
  const totRows  = allRows ?? rows;
  const showTotal = activeTab === "";
  const colCount = 7 + (prevMap !== null ? 2 : 0);
  return (
    <div style={{ overflowX: "auto", flex: 1 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
        <ColHdr hasCmp={prevMap !== null} />
        <tbody>
          {showTotal && totRows.length > 0 && <TotalRow rows={totRows} prevMap={prevMap} />}
          {rows.length === 0 && (
            <tr>
              <td colSpan={colCount} style={{ textAlign: "center", padding: 28, color: "#9CA3AF" }}>
                Sin registros
              </td>
            </tr>
          )}
          {rows.map((r, i) => (
            <DataRow key={i} r={r} i={i} prevMap={prevMap} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ScopeBnd = ({ filters }) => (
  <div
    style={{
      padding: "8px 20px",
      background: "#F8FAFC",
      borderBottom: "1px solid #E6E6E6",
      fontSize: 11.5,
      color: "#475569",
      display: "flex",
      gap: 6,
      alignItems: "center",
    }}
  >
    📊{" "}
    <span>
      Rango aplicado desde el dashboard:{" "}
      <strong>
        {filters?.FECHINIT ?? ""} – {filters?.FECHFINT ?? ""}
      </strong>
    </span>
  </div>
);

const CompBand = ({ filters, prevFilters, optLabel }) => (
  <div
    style={{
      padding: "10px 20px",
      background: "#EEF3FA",
      borderLeft: "4px solid #1B2F6E",
      borderBottom: "1px solid #E6E6E6",
      fontSize: 12,
      color: "#1B2F6E",
      display: "flex",
      alignItems: "center",
      gap: 8,
    }}
  >
    <span>📅</span>
    <span>
      <strong>{optLabel}:</strong>{" "}
      <strong>
        {filters?.FECHINIT} – {filters?.FECHFINT}
      </strong>
      {" vs "}
      <strong>
        {prevFilters?.FECHINIT} – {prevFilters?.FECHFINT}
      </strong>
    </span>
  </div>
);

const CtrlBar = ({ compareWith, onCompareChange, onDownload, showFilt, onToggleFilt }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "10px 20px",
      background: "#F8FAFC",
      borderBottom: "1px solid #E6E6E6",
      gap: 12,
      flexWrap: "wrap",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        background: "rgba(27,47,110,0.06)",
        borderRadius: 6,
        padding: "6px 12px",
        border: "1px solid #E6E6E6",
      }}
    >
      <span style={{ fontSize: 12, fontWeight: 700, color: "#1B2F6E", whiteSpace: "nowrap" }}>Comparar con:</span>
      <select
        value={compareWith}
        onChange={(e) => onCompareChange(e.target.value)}
        style={{
          background: "#fff",
          color: "#1B2F6E",
          border: "1px solid #E6E6E6",
          padding: "5px 10px",
          borderRadius: 4,
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
          minWidth: 270,
        }}
      >
        {COMPARE_OPTS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
    <div style={{ display: "flex", gap: 8 }}>
      <button
        onClick={onToggleFilt}
        style={{
          background: showFilt ? "#1B2F6E" : "#F1F5F9",
          color: showFilt ? "#fff" : "#475569",
          border: "1px solid #E6E6E6",
          borderRadius: 6,
          padding: "8px 14px",
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        🔍 Filtros top
      </button>
      <button
        onClick={onDownload}
        style={{
          background: "#E8472A",
          color: "#fff",
          border: "none",
          borderRadius: 6,
          padding: "8px 16px",
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
      Descargar Excel
    </button>
    </div>
  </div>
);

/**
 * Panel pantalla completa - Consolidado por cliente
 * @param {boolean}  isOpen
 * @param {function} onClose
 * @param {object}   filters       - filtros activos del dashboard
 * @param {array}    alertClients  - IDs de clientes de la alerta (null = todos)
 * @param {string}   gerenciaId   - CCOIDXXX de gerencia seleccionada desde el donut (null = todas)
 * @param {string}   gerenciaName - nombre de la gerencia para mostrar en el banner
 */
const Panclien = ({ isOpen, onClose, filters, alertClients, gerenciaId = null, gerenciaName = null }) => {
  const [activeTab, setActiveTab] = useState("");
  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [prevRows, setPrevRows] = useState([]);
  const [compareWith, setCompareWith] = useState("none");
  const [isLoading, setIsLoading] = useState(false);
  const [showFilt, setShowFilt] = useState(false);
  const [draftTopN, setDraftTopN] = useState(0);
  const [draftGer, setDraftGer] = useState("");
  const [draftGrup, setDraftGrup] = useState("");
  const [grupOpts, setGrupOpts] = useState([{ text: "SELECCIONE", value: "" }]);
  const [topN, setTopN] = useState(0);
  const [gerFilter, setGerFilter] = useState("");
  const [grupFilter, setGrupFilter] = useState("");
  const [appliedGrupOpts, setAppliedGrupOpts] = useState([]);
  const [getCliReport]           = useGetCliReportMutation();
  const [getCliKpiItems]         = useGetCliKpiItemsMutation();
  const [getCliKpiDeclaraciones] = useGetCliKpiDeclaracionesMutation();
  const [getCliKpiInspAuto]      = useGetCliKpiInspAutoMutation();
  const [getCliKpiInspFis]       = useGetCliKpiInspFisMutation();
  const [getCliKpiInspDoc]       = useGetCliKpiInspDocMutation();
  const [generateConsolidadoExcel] = useGenerateConsolidadoExcelMutation();
  const [getGrupos] = useLazyGetEntregauGruposQuery();
  const { data: gerenciasData } = useGetEntregauGerenciasQuery();
  const [page, setPage]     = useState(1);
  const [perPage, setPerPage] = useState(Number(process.env.REACT_APP_ROWS_PER_PAGE_TRACKING ?? 15));
  const optionsPerPage = JSON.parse(process.env.REACT_APP_OPTIONS_PER_PAGE_TRACKING ?? "[10,15,20,25]");
  const client = filters.CLIIDXXX ? filters.CLIIDXXX.split(" - ")[0] : "";
  const alertFilters = alertClients?.length > 0
    ? { ...filters, CLIIDXXX: client, CLIIDSXX: alertClients }
    : gerenciaId
      ? { ...filters, CLIIDXXX: client, CCOIDXXX: gerenciaId }
      : {...filters, CLIIDXXX: client};

  const runKpiChain = useCallback((filts) => {
    const upd  = (r) => setKpis((p) => p.map((k) => (k.label === r.label ? { ...k, value: r.value, loading: false } : k)));
    const fail = (l) => setKpis((p) => p.map((k) => (k.label === l ? { ...k, loading: false } : k)));
    [
      ["Ítems",         () => getCliKpiItems({ filters: filts }).unwrap()],
      ["Declaraciones", () => getCliKpiDeclaraciones({ filters: filts }).unwrap()],
      ["Insp. Auto",    () => getCliKpiInspAuto({ filters: filts }).unwrap()],
      ["Insp. Física",  () => getCliKpiInspFis({ filters: filts }).unwrap()],
      ["Insp. Doc.",    () => getCliKpiInspDoc({ filters: filts }).unwrap()],
    ].reduce(
      (chain, [label, fn]) => chain.then(() => fn().then(upd).catch(() => fail(label))),
      Promise.resolve()
    );
  }, [getCliKpiItems, getCliKpiDeclaraciones, getCliKpiInspAuto, getCliKpiInspFis, getCliKpiInspDoc]);

  useEffect(() => {
    if (!isOpen) {
      setShowFilt(false);
      setDraftTopN(0); setDraftGer(""); setDraftGrup("");
      setGrupOpts([{ text: "SELECCIONE", value: "" }]);
      setTopN(0); setGerFilter(""); setGrupFilter("");
      setAppliedGrupOpts([]);
      return;
    }
    setIsLoading(true);
    setActiveTab("");
    setPrevRows([]);
    getCliReport({ filters: alertFilters })
      .unwrap()
      .then((rMain) => {
        setRows(rMain.data ?? []);
        setKpis([
          ...(rMain.kpis ?? []).map((k) => ({ ...k, loading: false })),
          { label: "Ítems",         value: null, loading: true },
          { label: "Declaraciones", value: null, loading: true },
          { label: "Insp. Auto",    value: null, loading: true },
          { label: "Insp. Física",  value: null, loading: true },
          { label: "Insp. Doc.",    value: null, loading: true },
        ]);
        setIsLoading(false);
        runKpiChain(alertFilters);
      })
      .catch(() => setIsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, filters, alertClients, gerenciaId, getCliReport, runKpiChain]);

  useEffect(() => {
    if (!isOpen || compareWith === "none") {
      setPrevRows([]);
      return;
    }
    const pf = computePrevFilters(compareWith, filters);
    if (!pf) {
      setPrevRows([]);
      return;
    }
    getCliReport({ filters: pf })
      .unwrap()
      .then((r) => setPrevRows(r.data ?? []))
      .catch(() => setPrevRows([]));
  }, [isOpen, filters, getCliReport, compareWith]);

  useEffect(() => {
    setDraftGrup("");
    setGrupOpts([{ text: "SELECCIONE", value: "" }]);
    if (!draftGer || !gerenciasData?.data) { return; }
    const gerItem = gerenciasData.data.find((g) => g.CCOIDXXX === draftGer);
    if (!gerItem) { return; }
    getGrupos({ filters: { GRUPIDXX: gerItem.value } })
      .unwrap()
      .then((res) => setGrupOpts([{ text: "SELECCIONE", value: "" }, ...(res.data ?? [])]))
      .catch(() => {});
  }, [draftGer, gerenciasData, getGrupos]);

  useEffect(() => { setPage(1); }, [activeTab, topN, gerFilter, grupFilter, rows.length]);

  const handleToggleFilt = () => {
    if (!showFilt) {
      setDraftTopN(topN);
      setDraftGer(gerFilter);
      setDraftGrup(grupFilter);
      setGrupOpts(appliedGrupOpts.length > 0 ? appliedGrupOpts : [{ text: "SELECCIONE", value: "" }]);
    }
    setShowFilt((v) => !v);
  };

  const handleExcel = () => {
    const prevFilters  = compareWith !== "none" ? computePrevFilters(compareWith, filters) : null;
    const compareLabel = COMPARE_OPTS.find((o) => o.value === compareWith)?.label ?? "";
    generateConsolidadoExcel({ filters: alertFilters, prevFilters, compareLabel })
      .unwrap()
      .then(() =>
        shootToast({
          variant: "success",
          text: "Tu reporte se está generando! Recibirás confirmación por correo.",
          duration: 5000,
        }),
      )
      .catch(() => {});
  };

  const computedPrev = compareWith !== "none" ? computePrevFilters(compareWith, filters) : null;
  const activeCmpOpt = COMPARE_OPTS.find((o) => o.value === compareWith);
  const prevMap = buildPrevMap(prevRows);
  const tabCounts = TABS.map((t) => ({
    ...t,
    count: t.key ? rows.filter((r) => r.TIPOFILT === t.key).length : rows.length,
  }));
  const filteredRows = activeTab ? rows.filter((r) => r.TIPOFILT === activeTab) : rows;
  const hasCmp = compareWith !== "none" && prevRows.length > 0;
  const gerOpts = [
    { text: "Todas las gerencias", value: "" },
    ...(gerenciasData?.data ?? []).map((item) => ({ text: item.text, value: item.CCOIDXXX })),
  ];
  let displayRows = filteredRows;
  if (gerFilter && appliedGrupOpts.length > 1) {
    const validGroups = new Set(appliedGrupOpts.filter((g) => g.value).map((g) => g.text));
    displayRows = displayRows.filter((r) => validGroups.has(r.GRUPDESX));
  }
  if (grupFilter) {
    const grupText = appliedGrupOpts.find((g) => g.value === grupFilter)?.text;
    if (grupText) { displayRows = displayRows.filter((r) => r.GRUPDESX === grupText); }
  }
  if (topN > 0) {
    displayRows = [...displayRows].sort((a, b) => (b.INGPROPX ?? 0) - (a.INGPROPX ?? 0)).slice(0, topN);
  }

  const lastPage   = Math.max(1, Math.ceil(displayRows.length / perPage));
  const pageRows   = displayRows.slice((page - 1) * perPage, page * perPage);
  const metaPag    = { total: displayRows.length, per_page: perPage, current_page: page, last_page: lastPage };
  const pagItems   = generatePagination({ page, perPage, total: displayRows.length });

  const handlePrevPage  = () => { if (page > 1) { setPage(page - 1); } };
  const handleNextPage  = () => { if (page < lastPage) { setPage(page + 1); } };
  const handlePageClick = (ctx) => { if (typeof ctx.item.label === "number") { setPage(ctx.item.label); } };
  const handlePerPage   = (pp) => { setPerPage(Number(pp)); setPage(1); };

  return (
    <SlidePnl
      isOpen={isOpen}
      onClose={onClose}
      onBack={onClose}
      fullScreen
      dotColor="#E8472A"
      title="Consolidado por cliente"
      subtitle={`${filters?.FECHINIT ?? ""} – ${filters?.FECHFINT ?? ""} · ${rows.length} registros`}
      isLoading={isLoading}
    >
      <CtrlBar
        compareWith={compareWith}
        onCompareChange={setCompareWith}
        onDownload={handleExcel}
        showFilt={showFilt}
        onToggleFilt={handleToggleFilt}
      />
      {showFilt && (
        <FiltTop
          topN={draftTopN}
          onTopN={setDraftTopN}
          gerOpts={gerOpts}
          gerFilter={draftGer}
          onGer={setDraftGer}
          grupOpts={grupOpts}
          grupFilter={draftGrup}
          onGrup={setDraftGrup}
          onApply={() => {
            setTopN(draftTopN);
            setGerFilter(draftGer);
            setGrupFilter(draftGrup);
            setAppliedGrupOpts(grupOpts);
          }}
          onClear={() => {
            setDraftTopN(0);
            setDraftGer("");
            setDraftGrup("");
            setGrupOpts([{ text: "SELECCIONE", value: "" }]);
          }}
        />
      )}
      {alertClients?.length > 0 && (
        <div style={{ padding: "8px 20px", background: "#FEF3C7", borderBottom: "1px solid #F59E0B", fontSize: 11, color: "#92400E", fontWeight: 600 }}>
          ⚠️ Detalle de alerta — {alertClients.length} cliente{alertClients.length !== 1 ? "s" : ""} filtrado{alertClients.length !== 1 ? "s" : ""}
        </div>
      )}
      {gerenciaId && !alertClients?.length && (
        <div style={{ padding: "8px 20px", background: "#EFF6FF", borderBottom: "1px solid #93C5FD", fontSize: 11, color: "#1E40AF", fontWeight: 600 }}>
          🏢 Distribución por gerencia: <strong>{gerenciaName ?? gerenciaId}</strong>
        </div>
      )}
      {hasCmp ? (
        <CompBand filters={filters} prevFilters={computedPrev} optLabel={activeCmpOpt?.label} />
      ) : (
        <ScopeBnd filters={filters} />
      )}
      <KpiBar kpis={kpis} />
      <TabRow tabs={tabCounts} active={activeTab} onSelect={setActiveTab} />
      <DataTbl rows={pageRows} allRows={displayRows} activeTab={activeTab} prevMap={hasCmp ? prevMap : null} />
      <TableFooter>
        <TablePagination
          prevActive={page > 1}
          nextActive={page < lastPage}
          items={pagItems}
          onPrevClick={handlePrevPage}
          onNextClick={handleNextPage}
          onItemClick={handlePageClick}
          trackingMeta={metaPag}
          disableScroll={true}
        />
        <TableShowPages
          pages={optionsPerPage}
          onChangePage={handlePerPage}
          trackingMeta={metaPag}
          disableScroll={true}
        />
      </TableFooter>
    </SlidePnl>
  );
};

export default Panclien;
